/**
 * Boundary validation for API responses — where a body stops being `unknown` and
 * becomes a typed value.
 *
 * The shared api client casts response bodies blindly (`response.json() as T`), so a
 * type parameter there is an *assertion*, not a check: it tells TypeScript what to
 * believe and verifies nothing. Backend drift would then surface as `undefined` deep
 * inside a component, far from the cause. Parsing at the seam makes the guarantee
 * structural instead of a convention someone forgets once.
 *
 * Domain-free by design (A-04): each caller names its own source and route and
 * subclasses `ResponseContractError` so its failures stay identifiable — see
 * `shared/bql/contract.ts` and `features/custom-dashboards/wire-schema.ts`.
 */
import type { z } from 'zod'

/** How many issues to name before truncating — a full Zod dump is unreadable in a toast. */
const MAX_REPORTED_ISSUES = 3

/**
 * A response that arrived intact but did not match the schema its caller was built
 * against.
 *
 * Carries the `ZodError` as `cause` so the failing field stays identifiable all the
 * way to Sentry, while `message` names the source, the route and the first few
 * offending paths for anyone reading a toast or a log line.
 *
 * The `_isContractError` brand is load-bearing, not decoration: it is what
 * `smartRetry` keys off to treat this as terminal (see `isContractError`). Retrying a
 * schema mismatch just replays a deterministic failure.
 */
export class ResponseContractError extends Error {
	readonly _isContractError = true

	constructor(
		source: string,
		readonly route: string,
		override readonly cause: z.ZodError,
	) {
		super(
			`${source} response from ${route} did not match the contract: ${summarize(cause)}`,
		)
		this.name = 'ResponseContractError'
	}
}

/** Render the first few Zod issues as `path: message`, so the failing field is named up front. */
const summarize = (error: z.ZodError): string => {
	const issues = error.issues.slice(0, MAX_REPORTED_ISSUES).map((issue) => {
		const path = issue.path.length > 0 ? issue.path.join('.') : '<root>'
		return `${path}: ${issue.message}`
	})

	const remaining = error.issues.length - issues.length
	return remaining > 0
		? `${issues.join('; ')} (+${remaining} more)`
		: issues.join('; ')
}

/**
 * Parse a response body, raising the caller's own contract error when it does not
 * conform. `safeParse` rather than `.parse()` so the thrown error is branded for the
 * retry path and labelled with its route — while still keeping the original
 * `ZodError` intact underneath.
 *
 * `fail` is a callback rather than a class so each boundary keeps a distinct error
 * type its own tests and handlers can name.
 */
export const parseContract = <T>(
	schema: z.ZodType<T>,
	body: unknown,
	fail: (cause: z.ZodError) => ResponseContractError,
): T => {
	const result = schema.safeParse(body)

	if (!result.success) throw fail(result.error)

	return result.data
}
